/**
 * PixelService
 * Sends custom events to Recomdo pixel collector.
 */
import axios from 'axios';

const COLLECTOR_URL =
    (typeof process !== 'undefined' && process.env && process.env.COLLECTOR_URL)
    || (typeof window !== 'undefined' && window.__VOICE_SEARCH_ENV_SETTINGS__?.COLLECTOR_URL)
    || 'https://pixel.recomdo.ai/collect';
const CLIENT_ID_STORAGE_KEY = 'RECOMDO_CLIENT_ID';

let __recoSessionActive = false;
let __recoSessionId = null;
let __recoSearchCount = 0;
let __recoClickCount = 0;
let __recoSessionClientKey = null;

function isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getClientId() {
    try {
        if (!isBrowser()) return null;
        const stored = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
        if (stored) return stored;
        const cid = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, cid);
        return cid;
    } catch (_) {
        return null;
    }
}

function getEnvClientKey() {
    try {
        if (isBrowser()) {
            return (
                window.__VOICE_SEARCH_ENV_SETTINGS__?.REACT_APP_CLIENT_KEY ||
                window.__VOICE_SEARCH_CONFIG__?.clientKey ||
                ((typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLIENT_KEY) ? process.env.REACT_APP_CLIENT_KEY : null) ||
                null
            );
        }
        return (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLIENT_KEY) ? process.env.REACT_APP_CLIENT_KEY : null;
    } catch (_) {
        return null;
    }
}

function getCookie(name) {
    try {
        return document.cookie
            .split(';')
            .map(s => s.trim())
            .find(s => s.startsWith(name + '='))
            ?.split('=')[1] || null;
    } catch (_) {
        return null;
    }
}

function getRecoUid() {
    try {
        if (!isBrowser()) return null;
        const fromWindow = typeof window.__RECOMDO_UID__ === 'string' ? window.__RECOMDO_UID__ : null;
        if (fromWindow) return fromWindow;
        const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('RECOMDO_UID') : null;
        if (ls) return ls;
        const ss = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('RECOMDO_UID') : null;
        if (ss) return ss;
        const cookieVal = getCookie('reco_uid');
        return cookieVal || null;
    } catch (_) {
        return null;
    }
}

function getEmailSha256() {
    try {
        if (!isBrowser()) return null;
        let v = null;
        try {
            v = (typeof localStorage !== 'undefined') ? localStorage.getItem('RECOMDO_EMAIL_SHA256_CACHE') : null;
        } catch (_) {
        }
        if (!v) {
            try {
                v = (typeof sessionStorage !== 'undefined') ? sessionStorage.getItem('RECOMDO_EMAIL_SHA256_CACHE') : null;
            } catch (_) {
            }
        }
        if (v && typeof v === 'string') {
            const trimmed = v.trim();
            return trimmed.length ? trimmed : null;
        }
        return null;
    } catch (_) {
        return null;
    }
}

function basePayload() {
    const client_id = getClientId();
    const client_key = __recoSessionActive && __recoSessionClientKey
        ? __recoSessionClientKey
        : getEnvClientKey();
    const reco_uid = getRecoUid();
    const email_sha256 = getEmailSha256();
    let page_url = null;
    let referrer = null;
    try {
        if (isBrowser()) {
            page_url = window.location?.href || null;
            referrer = document.referrer || null;
        }
    } catch (_) {
    }
    return {
        timestamp: new Date().toISOString(),
        page_url,
        referrer,
        session_id: __recoSessionActive ? __recoSessionId : null,
        consent: null,
        client_id,
        client_key,
        reco_uid,
        person_meta: {source: email_sha256 ? 'email' : 'uid'},
        user_id: null,
        email_sha256,
    };
}

async function sendEvent(payload) {
    try {
        await axios.post(COLLECTOR_URL, payload, {
            headers: {
                'content-type': 'application/json',
            },
            timeout: 3000,
        });
    } catch (e) {
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
            console.warn('Pixel send failed:', e?.message || e);
        }
    }
}

/**
 * Start a recommendation session (when the modal opens)
 * Optionally accept a specific clientKey to pin for the session
 */
export function startRecoSession(clientKey) {
    try {
        if (__recoSessionActive) {
            __recoSessionActive = false;
        }
        __recoSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        __recoSearchCount = 0;
        __recoClickCount = 0;
        __recoSessionClientKey = clientKey || getEnvClientKey() || null;
        __recoSessionActive = true;
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                window.sessionStorage.setItem('reco_session_active', '1');
            }
        } catch (_) {
        }
    } catch (_) {
    }
}

/**
 * End the recommendation session and send the reco_session pixel (when the modal closes)
 */
export function endRecoSessionAndSend(preferBeacon = false) {
    try {
        if (!__recoSessionActive) return;
        const payload = {
            type: 'reco_session',
            ...basePayload(),
            session_id: __recoSessionId,
            client_key: __recoSessionClientKey || getEnvClientKey() || undefined,
            reco_search_count: __recoSearchCount,
            reco_click_count: __recoClickCount,
        };
        if (preferBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            try {
                const body = new Blob([JSON.stringify(payload)], {type: 'application/json'});
                const ok = navigator.sendBeacon(COLLECTOR_URL, body);
                if (!ok) {
                    sendEvent(payload);
                }
            } catch (_) {
                sendEvent(payload);
            }
        } else {
            sendEvent(payload);
        }
    } finally {
        __recoSessionActive = false;
        __recoSessionId = null;
        __recoSearchCount = 0;
        __recoClickCount = 0;
        __recoSessionClientKey = null;
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                window.sessionStorage.removeItem('reco_session_active');
            }
        } catch (_) {
        }
    }
}

/**
 * Send reco_search event when search results are returned
 * @param {string} searchPhrase
 * @param {Array<any>} results optional, may be used for counts/ids
 */
export function sendRecoSearch(searchPhrase, results) {
    if (!searchPhrase || typeof searchPhrase !== 'string') return;
    const payload = {
        type: 'reco_search',
        ...basePayload(),
        search_phrase: searchPhrase,
        results_count: Array.isArray(results) ? results.length : null,
    };
    if (__recoSessionActive) {
        __recoSearchCount += 1;
    }
    sendEvent(payload);
}

/**
 * Send reco_click event when a product is clicked
 * @param {Object} product
 * @param {Object} meta optional metadata
 * @param {string} clientKey optional explicit client key
 */
export function isRecoSessionActive() {
    return __recoSessionActive;
}

export function sendRecoClick(product, meta = {}, clientKey) {
    if (!product || typeof product !== 'object') return;

    const mapped = {
        id: product.id || product.entity_id || product.product_id || null,
        title: product.title || product.name || null,
        price: typeof product.price === 'number' ? product.price : (parseFloat(product.price) || null),
        currency: product.currency || (isBrowser() ? (window.__VOICE_SEARCH_CONFIG__?.currency || null) : null),
        sku: product.sku || null,
        vendor: product.vendor || product.brand || null,
        image: product.image || product.thumbnail || null,
        url: product.url_key || product.url || null,
    };

    const resolvedClientKey = clientKey || meta.clientKey || getEnvClientKey() || undefined;
    if (__recoSessionActive && resolvedClientKey && !__recoSessionClientKey) {
        __recoSessionClientKey = resolvedClientKey;
    }

    const payload = {
        type: 'reco_click',
        ...basePayload(),
        client_key: resolvedClientKey,
        reco_src: meta.reco_src || undefined,
        reco_q: meta.reco_q || undefined,
        reco_pos: typeof meta.reco_pos !== 'undefined' ? meta.reco_pos : undefined,
        reco_rid: meta.reco_rid || undefined,
        product: mapped,
    };
    if (__recoSessionActive) {
        __recoClickCount += 1;
    }
    sendEvent(payload);
}
export function sendRecoAddToCart(product, meta = {}, clientKey) {
    if (!product || typeof product !== 'object') return;

    const mapped = {
        id: product.id || product.entity_id || product.product_id || null,
        title: product.title || product.name || null,
        price: typeof product.price === 'number' ? product.price : (parseFloat(product.price) || null),
        currency: product.currency || (isBrowser() ? (window.__VOICE_SEARCH_CONFIG__?.currency || null) : null),
        sku: product.sku || null,
        vendor: product.vendor || product.brand || null,
        image: product.image || product.thumbnail || null,
        url: product.url_key || product.url || null,
    };

    const resolvedClientKey = clientKey || meta.clientKey || getEnvClientKey() || undefined;
    if (__recoSessionActive && resolvedClientKey && !__recoSessionClientKey) {
        __recoSessionClientKey = resolvedClientKey;
    }
    const payload = {
        type: 'add_to_cart',
        ...basePayload(),
        client_key: resolvedClientKey,
        reco_src: meta.reco_src || undefined,
        reco_q: meta.reco_q || undefined,
        reco_pos: typeof meta.reco_pos !== 'undefined' ? meta.reco_pos : undefined,
        reco_rid: meta.reco_rid || undefined,
        product: mapped,
    };
    if (__recoSessionActive) {
        __recoClickCount += 1;
    }
    sendEvent(payload);
}